import type { BookingPreferences, SelectedSlot } from "../bookingFlowMachine"
import { bookingIneligibilityReason, isBookingConsentRequired, isBookingSessionExpired } from "../errorReporting"
import {
  AVAILABILITY_FOR_DATE_QUERY_KEY,
  AVAILABLE_DATES_QUERY_KEY,
  BOOKING_OFFERING_QUERY_KEY,
  BookedAppointment,
  ScreeningProgram,
  useBookAppointment,
  useBookingOffering
} from "../hooks"
import { appointmentTypeCopy, visitTitle } from "../preferencesCopy"
import { AppointmentAvailability } from "./AppointmentAvailability"
import { UsaStatesAndDc } from "@internal/constants"
import { ButtonSkeleton, Skeleton } from "@internal/ui"
import {
  Alert,
  BackIcon,
  Box,
  ForwardIcon,
  IconWithBackground,
  createLucideIcon,
  Tile,
  Typography
} from "@carrotfertility/carotene-core"
import {
  Form,
  FormAutocomplete,
  FormButton,
  type FormSelectOption,
  useFormContext,
  useWatch
} from "@carrotfertility/carotene-core-x"
import { useQueryClient } from "@tanstack/react-query"
import React from "react"
import { useIntl } from "react-intl"
import { BookingBreadcrumbs } from "../Breadcrumbs"
import { Heart } from "lucide-react"
import { useTrackBookingScreenView, useBookingTracking } from "../tracking"
import { getDisplayTimeZone } from "../timezone"

const HeartIcon = createLucideIcon(Heart, "HeartIcon")

type SelectPreferencesPageProps = {
  program: ScreeningProgram
  enterprisePaymentsEnabled: boolean
  onContinueToPayment: (preferences: BookingPreferences, slot: SelectedSlot) => void | Promise<void>
  paymentInitialization?: { isPending: boolean; isError: boolean }
  onConfirmBooking: (preferences: BookingPreferences, slot: SelectedSlot, appointment: BookedAppointment) => void
  onDisqualified: (exclusionMessageId: string) => void
  onNeedsConsent: () => void
  onBack: () => void
  timeZone?: string
}

type PreferencesFormValues = {
  offeringId: string
  // FormAutocomplete stores the selected option object (or null), not a bare value.
  region: FormSelectOption | null
  // Set by AppointmentAvailability once the member picks a date + time.
  selectedDate: string | null
  selectedSlot: SelectedSlot | null
}

// The screen's own copy is English literal (see preferencesCopy.ts); the location-of-care
// options are localized here from the shared @internal/constants state list.
export function SelectPreferencesPage({
  program,
  enterprisePaymentsEnabled,
  onContinueToPayment,
  paymentInitialization,
  onConfirmBooking,
  onDisqualified,
  onNeedsConsent,
  onBack,
  timeZone
}: SelectPreferencesPageProps): JSX.Element {
  const intl = useIntl()
  const offeringQuery = useBookingOffering(program)
  const bookAppointment = useBookAppointment()
  const queryClient = useQueryClient()
  const [availabilityEpoch, setAvailabilityEpoch] = React.useState(0)
  const tracking = useBookingTracking()
  const resolvedOffering =
    offeringQuery.data?.offerings.find((offering) => offering.bookable) ?? offeringQuery.data?.offerings[0]
  useTrackBookingScreenView(
    !offeringQuery.isLoading && !offeringQuery.isError && resolvedOffering
      ? { screen: "scheduling", appointmentType: resolvedOffering.appointmentType }
      : null
  )

  const header = (
    <>
      <Box sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.sm) })}>
        <BookingBreadcrumbs />
        <Alert severity="error" sx={(theme) => ({ marginBlockStart: theme.spacing(theme.tokens.spacing.lg) })}>
          If you are experiencing a medical emergency, call emergency services immediately.
        </Alert>
      </Box>

      <Typography variant="h2" component="h1">
        Let&apos;s find a time that works
      </Typography>
    </>
  )

  if (offeringQuery.isLoading) {
    return (
      <PreferencesLayout>
        {header}
        <PreferencesSkeleton />
      </PreferencesLayout>
    )
  }

  if (offeringQuery.isError || !offeringQuery.data) {
    return <Alert severity="error">Something went wrong loading your options. Please try again.</Alert>
  }

  const bookingOffering = offeringQuery.data
  const offerings = bookingOffering.offerings
  // The summary line and default selection follow the primary bookable offering (the initial
  // visit until the member has completed one, then the follow-up).
  const primaryOffering = offerings.find((offering) => offering.bookable) ?? offerings[0]

  // 50 states + DC only: a clinician cannot hold a state medical license in a territory, so
  // offering one would be a dead end.
  const regionOptions = UsaStatesAndDc.map((state) => ({ value: state.id, label: intl.formatMessage(state.label) }))

  const shouldUseVisitPayments = (offeringId: string): boolean =>
    enterprisePaymentsEnabled && offerings.find((offering) => offering.id === offeringId)?.requiresPayment === true

  const handleSubmit = async (values: PreferencesFormValues) => {
    // The submit button is disabled until a slot is chosen; guard anyway.
    if (!values.selectedSlot) return
    const slot = values.selectedSlot
    const selectedOffering = offerings.find((offering) => offering.id === values.offeringId)
    if (!selectedOffering?.bookable) return
    try {
      if (shouldUseVisitPayments(values.offeringId)) {
        await onContinueToPayment(
          { offeringId: values.offeringId, region: values.region ? String(values.region.value) : "" },
          slot
        )
        return
      }
      const attemptNumber = tracking.onBookingSubmitted(primaryOffering.appointmentType)
      const displayTimeZone = getDisplayTimeZone(timeZone)
      // Place the booking, then advance — the confirmation screen needs the returned
      // appointment (assigned provider + visit window). A failure surfaces inline (see
      // AppointmentAvailability) and leaves the member here to retry.
      const appointment = await bookAppointment.mutateAsync({
        program,
        slotToken: slot.slotToken,
        routingBackend: slot.routingBackend,
        // The member doesn't pick a provider; take the first eligible host for the slot.
        hostId: slot.availableHostIds[0],
        timeZone: displayTimeZone
      })
      tracking.onEvent({
        type: "success",
        appointmentId: appointment.appointmentId,
        hostId: appointment.hostId,
        bookingAttemptNumber: attemptNumber
      })
      onConfirmBooking(
        {
          offeringId: values.offeringId,
          region: values.region ? String(values.region.value) : ""
        },
        slot,
        appointment
      )
    } catch (error) {
      // Eligibility rejection → out; unsigned-consent gate → back to consent; anything else → inline retry.
      // Both mutations reject on failure. Catch ordinary booking/payment errors here; their mutation
      // error state (including paymentInitialization.isError) drives AppointmentAvailability's inline
      // message. Payment retries retain the bookingSessionId in the flow context.
      const ineligibleReason = bookingIneligibilityReason(error)
      if (ineligibleReason !== null) {
        tracking.onEvent({ type: "gating_result", result: "ineligible", resultSource: "booking_recheck" })
        onDisqualified(ineligibleReason)
      } else if (isBookingConsentRequired(error)) {
        onNeedsConsent()
      } else if (isBookingSessionExpired(error)) {
        void queryClient.invalidateQueries({ queryKey: [AVAILABLE_DATES_QUERY_KEY] })
        void queryClient.invalidateQueries({ queryKey: [AVAILABILITY_FOR_DATE_QUERY_KEY] })
        // defaultValues lock offeringId at mount. Load the current backend's offering before
        // remounting, or the next availability read is rejected for the previous id.
        try {
          await queryClient.refetchQueries({ queryKey: [BOOKING_OFFERING_QUERY_KEY, program] })
        } catch {
          return
        }
        setAvailabilityEpoch((epoch) => epoch + 1)
      }
    }
  }

  return (
    <Form<PreferencesFormValues>
      key={availabilityEpoch}
      onSubmit={handleSubmit}
      defaultValues={{ offeringId: primaryOffering?.id ?? "", region: null, selectedDate: null, selectedSlot: null }}
    >
      <PreferencesLayout>
        {header}

        <Tile
          variant="primary"
          sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.sm) })}
        >
          <Box sx={(theme) => ({ display: "flex", alignItems: "center", gap: theme.spacing(theme.tokens.spacing.sm) })}>
            <IconWithBackground color="primary" icon={HeartIcon} />
            <Typography variant="h3">
              {visitTitle(primaryOffering.durationMinutes, primaryOffering.appointmentType)}
            </Typography>
          </Box>
          <Typography variant="body1">{appointmentTypeCopy(primaryOffering.appointmentType).description}</Typography>
        </Tile>

        <Box sx={{ maxInlineSize: 440 }}>
          <FormAutocomplete
            name="region"
            label="Please select the state where you'll be during your visit"
            controllerProps={{ rules: { required: "Please select your location of care." } }}
            fullWidth
            options={regionOptions}
            // MUI's InputLabel is single-line with an ellipsis, which cuts this long label off on phones.
            // Carotene derives `required` from the rule, so the asterisk is hidden here instead; the
            // field stays required for validation and aria-required.
            sx={{
              "& .MuiInputLabel-root": { whiteSpace: "normal" },
              "& .MuiFormLabel-asterisk": { display: "none" }
            }}
          />
        </Box>

        <AvailabilityStep
          program={program}
          shouldUseVisitPayments={shouldUseVisitPayments}
          onBack={onBack}
          booking={{
            isPending: bookAppointment.isLoading || paymentInitialization?.isPending === true,
            isError: bookAppointment.isError || paymentInitialization?.isError === true
          }}
          timeZone={timeZone}
        />
      </PreferencesLayout>
    </Form>
  )
}

function PreferencesLayout({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(theme.tokens.spacing.lg),
        maxInlineSize: 640
      })}
    >
      {children}
    </Box>
  )
}

function PreferencesSkeleton(): JSX.Element {
  return (
    <Box
      role="status"
      aria-label="Loading your appointment options"
      aria-busy="true"
      sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.lg) })}
    >
      <Skeleton width="100%" height={144} sx={(theme) => ({ borderRadius: theme.tokens.borderRadius.md })} />

      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(theme.tokens.spacing.xs),
          maxInlineSize: 440
        })}
      >
        <Skeleton width={180} height={16} />
        <Skeleton width="100%" height={56} />
      </Box>

      <Box
        sx={(theme) => ({
          display: "flex",
          justifyContent: "space-between",
          gap: theme.spacing(theme.tokens.spacing.sm)
        })}
      >
        <ButtonSkeleton width={96} />
        <ButtonSkeleton width={144} />
      </Box>
    </Box>
  )
}

// Two-phase, on one screen: the member first submits their visit type + location ("See
// availability"), which reveals the date + time picker (AppointmentAvailability owns that and
// the booking/payment CTA). Lives inside <Form> so it can validate + read the
// preference fields. Editing the visit type or location after revealing hides availability
// again — the shown slots were for the old preferences.
function AvailabilityStep({
  program,
  shouldUseVisitPayments,
  onBack,
  booking,
  timeZone
}: {
  program: ScreeningProgram
  shouldUseVisitPayments: (offeringId: string) => boolean
  onBack: () => void
  booking: { isPending: boolean; isError: boolean }
  timeZone?: string
}): JSX.Element {
  const { trigger, getValues, setValue } = useFormContext<PreferencesFormValues>()
  const offeringId = useWatch<PreferencesFormValues>({ name: "offeringId" })
  const region = useWatch<PreferencesFormValues>({ name: "region" })
  const stateCode = region ? String(region.value) : ""
  const tracking = useBookingTracking()

  // The preferences the member submitted to reveal availability; null until they do.
  const [submitted, setSubmitted] = React.useState<{ offeringId: string; stateCode: string } | null>(null)
  React.useEffect(() => {
    if (!submitted) tracking.onEvent({ type: "availability", searchKey: "", outcome: null })
  }, [submitted, tracking])

  React.useEffect(() => {
    if (submitted && (submitted.offeringId !== offeringId || submitted.stateCode !== stateCode)) {
      setSubmitted(null)
      setValue("selectedDate", null)
      setValue("selectedSlot", null)
    }
  }, [offeringId, stateCode, submitted, setValue])

  const onSeeAvailability = async () => {
    // Submitting the preferences: validate the required fields, then reveal availability.
    const valid = await trigger(["offeringId", "region"])
    if (!valid) return
    const values = getValues()
    setSubmitted({
      offeringId: values.offeringId,
      stateCode: values.region ? String(values.region.value) : ""
    })
  }

  const backToPreferences = () => {
    setSubmitted(null)
    setValue("region", null)
    setValue("selectedDate", null)
    setValue("selectedSlot", null)
  }

  if (!submitted) {
    return (
      <Box
        sx={(theme) => ({
          display: "flex",
          justifyContent: "space-between",
          gap: theme.spacing(theme.tokens.spacing.sm)
        })}
      >
        <FormButton color="secondary" variant="text" startIcon={<BackIcon />} onClick={onBack}>
          Back
        </FormButton>
        <FormButton type="submit" color="secondary" endIcon={<ForwardIcon />} onClick={onSeeAvailability}>
          Continue
        </FormButton>
      </Box>
    )
  }

  return (
    <AppointmentAvailability
      program={program}
      offeringId={submitted.offeringId}
      stateCode={submitted.stateCode}
      booking={booking}
      continueToPayment={shouldUseVisitPayments(offeringId)}
      onBack={backToPreferences}
      timeZone={timeZone}
    />
  )
}
